import { ArrowSquareOutIcon } from "@phosphor-icons/react/dist/csr/ArrowSquareOut";
import { FileMagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/FileMagnifyingGlass";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { InputGroupInput } from "@volca/ui/components/ui/input-group";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import type { AiSpreadsheetValueType } from "@/payroll/ai-script/types";
import type { AiScriptCellLink } from "./ai-script-spreadsheet-viewer-types";
import { CellFormatMenu } from "./cell-format-menu";
import { FormulaBarStatus } from "./formula-bar-status";
import { FullScreenToggle } from "./full-screen-toggle";
import { UndoRedoControls } from "./undo-redo-controls";
import { ViewEditButton } from "./view-edit-button";

interface FormulaBarProps {
  coordinate: string | null;
  commitTarget: FormulaBarCommitTarget | null;
  value: string;
  errorCode: string | null;
  canEdit: boolean;
  // The blank row under the data is selected — committing adds a row.
  addRowMode?: boolean;
  isSheetReadOnly?: boolean;
  isCellLocked?: boolean;
  isRecalculating: boolean;
  hasUnsavedChanges?: boolean;
  isRetryingUnsavedChanges?: boolean;
  onRetryUnsavedChanges?: () => void;
  operationError?: string | null;
  onRetryOperation?: () => void;
  isMaximized: boolean;
  rightAccessory?: ReactNode;
  // The selected cell's current value_type — shown as a format indicator in the
  // toolbar. Null when nothing (or a cell without a type) is selected.
  valueType?: string | null;
  // Change the selected cell's format. When provided (and the cell is editable),
  // the format indicator becomes a clickable menu.
  onFormatChange?: (valueType: AiSpreadsheetValueType) => void;
  // When the selected cell carries a persistent edit, a "View edit" segment is
  // shown that opens the manual-edits sidebar focused on that cell.
  onViewEdit?: () => void;
  cellLink?: AiScriptCellLink | null;
  onShowSource?: (() => void) | null;
  onCommit: (rawInput: string, target: FormulaBarCommitTarget) => void;
  onToggleMaximize: () => void;
  // Undo/redo controls. Rendered only when both handlers are provided; each
  // button is disabled when there's nothing to act on (or a replay is running).
  // The labels ("Undo edit cell B4") drive the tooltip copy.
  canUndo?: boolean;
  canRedo?: boolean;
  undoLabel?: string | null;
  redoLabel?: string | null;
  // A replay is in flight — disable both controls (the "Calculating…" indicator
  // above already reflects the in-progress mutation).
  isUndoRedoApplying?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
}

export type FormulaBarCommitTarget =
  | { kind: "cell"; cellId: string }
  | { kind: "new-row"; columnIndex: number };

interface FormulaBarEditSession {
  committed: boolean;
  coordinate: string | null;
  startingValue: string;
  target: FormulaBarCommitTarget;
}

// Formula bar mirroring Excel: cell reference on the left, the cell's raw input
// / formula on the right. When a real cell is selected it is editable and
// commits the same way as in-grid edits (Enter commits, Escape reverts).
export const FormulaBar = ({
  coordinate,
  commitTarget,
  value,
  errorCode,
  canEdit,
  addRowMode = false,
  isSheetReadOnly = false,
  isCellLocked = false,
  isRecalculating,
  hasUnsavedChanges,
  isRetryingUnsavedChanges = false,
  onRetryUnsavedChanges,
  operationError,
  onRetryOperation,
  isMaximized,
  rightAccessory,
  valueType,
  onFormatChange,
  onViewEdit,
  cellLink,
  onShowSource,
  onCommit,
  onToggleMaximize,
  canUndo = false,
  canRedo = false,
  undoLabel,
  redoLabel,
  isUndoRedoApplying = false,
  onUndo,
  onRedo,
}: FormulaBarProps) => {
  const [draft, setDraft] = useState(value);
  const editSessionRef = useRef<FormulaBarEditSession | null>(null);

  // Keep an active edit bound to the cell where it started. A grid click can
  // change these props before the input blur event commits the draft.
  useEffect(() => {
    if (!editSessionRef.current) setDraft(value);
  }, [value, coordinate]);

  const commit = () => {
    const editSession = editSessionRef.current;
    if (
      !editSession ||
      editSession.committed ||
      draft === editSession.startingValue
    )
      return;

    editSession.committed = true;
    onCommit(draft, editSession.target);
  };

  return (
    <Stack
      direction="row"
      align="stretch"
      gap={0}
      className="w-full border-b border-line bg-surface-sunken"
    >
      <Stack
        align="start"
        justify="center"
        gap={0}
        className="w-24 shrink-0 border-r border-line px-3 text-sm font-medium text-subtle"
      >
        {coordinate ?? ""}
      </Stack>
      <Stack
        align="center"
        justify="center"
        gap={0}
        className="w-9 shrink-0 border-r border-line font-serif text-sm text-subtle italic"
      >
        fx
      </Stack>
      <InputGroupInput
        value={draft}
        readOnly={!canEdit}
        onFocus={() => {
          editSessionRef.current =
            canEdit && commitTarget
              ? {
                  committed: false,
                  coordinate,
                  startingValue: value,
                  target: commitTarget,
                }
              : null;
        }}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit();
            event.currentTarget.blur();
          } else if (event.key === "Escape") {
            const editSession = editSessionRef.current;
            if (editSession) editSession.committed = true;
            setDraft(editSession?.startingValue ?? value);
            event.currentTarget.blur();
          }
        }}
        onBlur={() => {
          const editSession = editSessionRef.current;
          commit();
          editSessionRef.current = null;
          if (editSession?.coordinate !== coordinate) setDraft(value);
        }}
        placeholder={
          addRowMode
            ? "Type a value and press Enter to add a row"
            : canEdit
              ? "Edit this cell's value or formula"
              : isSheetReadOnly
                ? "Select a cell to view its value or formula (view only)"
                : "Select a cell to view its value or formula"
        }
        // No focus ring, matching the search field: the caret is the indicator
        // in a text input, and a 2px box drawn around a field this wide read as
        // chrome rather than as state. The toolbar's icon buttons keep their
        // rings — they have nothing else to show focus with.
        className={cn("min-w-0 px-3 text-ink", {
          "text-danger": !!errorCode,
        })}
      />
      {(cellLink || onShowSource) && (
        <Stack
          direction="row"
          align="center"
          gap={0}
          className="max-w-96 shrink-0 px-1"
        >
          {cellLink && (
            <Button
              variant="link"
              size="sm"
              className="min-w-0"
              onClick={cellLink.onOpen}
            >
              <ArrowSquareOutIcon data-icon="inline-start" />
              <span className="truncate">Open {cellLink.label}</span>
            </Button>
          )}
          {onShowSource && (
            <Button
              variant="link"
              size="sm"
              className="shrink-0"
              onClick={onShowSource}
            >
              <FileMagnifyingGlassIcon data-icon="inline-start" />
              Show source
            </Button>
          )}
        </Stack>
      )}
      <FormulaBarStatus
        coordinate={coordinate}
        errorCode={errorCode}
        isCellLocked={isCellLocked}
        isSheetReadOnly={isSheetReadOnly}
        isRecalculating={isRecalculating}
        hasUnsavedChanges={hasUnsavedChanges}
        isRetryingUnsavedChanges={isRetryingUnsavedChanges}
        onRetryUnsavedChanges={onRetryUnsavedChanges}
        operationError={operationError}
        onRetryOperation={onRetryOperation}
      />
      {coordinate && (
        <CellFormatMenu
          valueType={valueType}
          onFormatChange={canEdit ? onFormatChange : undefined}
        />
      )}
      {onViewEdit && <ViewEditButton onViewEdit={onViewEdit} />}
      {onUndo && onRedo && (
        <UndoRedoControls
          canUndo={canUndo}
          canRedo={canRedo}
          undoLabel={undoLabel}
          redoLabel={redoLabel}
          isApplying={isUndoRedoApplying}
          onUndo={onUndo}
          onRedo={onRedo}
        />
      )}
      {rightAccessory}
      <FullScreenToggle
        isMaximized={isMaximized}
        onToggleMaximize={onToggleMaximize}
      />
    </Stack>
  );
};
